'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { MapPin, Clock, Star, Heart, Package, Loader2 } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Listing } from './feedData';

const conditionMap: Record<string, { label: string; cls: string }> = {
  new: { label: 'New', cls: 'badge-new' },
  'like-new': { label: 'Like New', cls: 'badge-like-new' },
  used: { label: 'Used', cls: 'badge-used' },
  'well-used': { label: 'Well Used', cls: 'badge-well-used' },
};

type Props = {
  listings: Listing[];
  loading: boolean;
  onReset: () => void;
  hasFilters: boolean;
};

function ListingCard({ listing }: { listing: Listing }) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);

  const checkSaved = useCallback(async () => {
    if (!user) return;
    const supabase = createClient();
    const { data } = await supabase
      .from('saved_listings')
      .select('id')
      .eq('user_id', user.id)
      .eq('listing_id', listing.id)
      .maybeSingle();
    if (data) {
      setSaved(true);
      setSavedId(data.id);
    }
  }, [user, listing.id]);

  useEffect(() => {
    checkSaved();
  }, [checkSaved]);

  async function handleToggleSave(e: React.MouseEvent) {
    e.preventDefault();
    if (!user) {
      window.location.href = '/login?next=/listings-feed';
      return;
    }
    setSaveLoading(true);
    const supabase = createClient();
    if (saved && savedId) {
      await supabase.from('saved_listings').delete().eq('id', savedId).eq('user_id', user.id);
      setSaved(false);
      setSavedId(null);
    } else {
      const { data } = await supabase
        .from('saved_listings')
        .insert({ user_id: user.id, listing_id: listing.id })
        .select('id')
        .single();
      if (data) {
        setSaved(true);
        setSavedId(data.id);
      }
    }
    setSaveLoading(false);
  }

  const cond = conditionMap[listing.condition] ?? { label: listing.condition, cls: 'badge-used' };
  const firstImage = listing.images?.[0];
  const sellerDisplay = listing.seller?.store_name || listing.seller?.full_name || 'Seller';
  const sellerInitials = sellerDisplay.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
  const timeAgo = listing.created_at
    ? (() => {
        const diff = Date.now() - new Date(listing.created_at).getTime();
        const h = Math.floor(diff / 3600000);
        if (h < 1) return 'Just now';
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        return `${d}d ago`;
      })()
    : '';

  return (
    <div className="listing-card group relative">
      <button
        onClick={handleToggleSave}
        disabled={saveLoading}
        className={`absolute top-2 left-2 z-10 w-7 h-7 flex items-center justify-center rounded-full backdrop-blur-sm transition-all duration-150 ${saved ? 'bg-rose-500 text-white' : 'bg-black/40 text-white/60 hover:text-white hover:bg-black/60'}`}
        aria-label={saved ? 'Remove from saved' : 'Save listing'}
      >
        {saveLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Heart className={`w-3.5 h-3.5 ${saved ? 'fill-white' : ''}`} />}
      </button>

      <Link href={`/listing-detail?id=${listing.id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden bg-[hsl(222,35%,10%)]">
          {firstImage ? (
            <AppImage
              src={firstImage.src}
              alt={firstImage.alt || listing.title}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              className="object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-8 h-8 text-slate-600" />
            </div>
          )}
          <div className="absolute top-2 right-2">
            <span className={cond.cls}>{cond.label}</span>
          </div>
        </div>

        <div className="p-3.5">
          <h3 className="text-sm font-semibold text-white leading-snug line-clamp-2 group-hover:text-[hsl(28,85%,58%)] transition-colors duration-150 mb-2">
            {listing.title}
          </h3>
          <div className="text-lg font-bold text-white tabular-nums mb-2.5">
            £{listing.price?.toLocaleString()}
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500 mb-2.5">
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {listing.location || listing.region}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeAgo}
            </div>
          </div>
          <div className="flex items-center justify-between pt-2.5 border-t border-[hsl(222,25%,18%)]">
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-[hsl(28,85%,48%)]/20 border border-[hsl(28,85%,48%)]/30 flex items-center justify-center text-[hsl(28,85%,48%)] text-[9px] font-bold">
                {sellerInitials}
              </div>
              <span className="text-xs text-slate-500 truncate max-w-[80px]">{sellerDisplay.split(' ')[0]}</span>
            </div>
            {listing.seller?.rating ? (
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Star className="w-3 h-3 text-[hsl(28,85%,48%)] fill-[hsl(28,85%,48%)]" />
                {listing.seller.rating}
              </div>
            ) : null}
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function ListingsGrid({ listings, loading, onReset, hasFilters }: Props) {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Loader2 className="w-8 h-8 text-[hsl(28,85%,48%)] animate-spin mb-4" />
        <p className="text-sm text-slate-500">Loading listings...</p>
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-14 h-14 rounded-2xl bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] flex items-center justify-center mb-4">
          <Package className="w-6 h-6 text-slate-500" />
        </div>
        {hasFilters ? (
          <>
            <h3 className="text-base font-semibold text-white mb-2">No matching listings found</h3>
            <p className="text-sm text-slate-500 max-w-xs mb-5">
              Try adjusting your filters or search term to see more results.
            </p>
            <button
              onClick={onReset}
              className="flex items-center gap-2 bg-[hsl(28,85%,48%)] hover:bg-[hsl(28,85%,41%)] text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition-all duration-150 active:scale-95"
            >
              Clear all filters
            </button>
          </>
        ) : (
          <>
            <h3 className="text-base font-semibold text-white mb-2">No listings in this category yet</h3>
            <p className="text-sm text-slate-500 max-w-xs mb-5">
              Be the first to list your working dog equipment here.
            </p>
            <Link
              href="/create-listing"
              className="flex items-center gap-2 bg-[hsl(28,85%,48%)] hover:bg-[hsl(28,85%,41%)] text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition-all duration-150 active:scale-95"
            >
              Create Listing
            </Link>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {listings.map((listing) => (
          <ListingCard key={listing.id} listing={listing} />
        ))}
      </div>
    </div>
  );
}